import { useEffect, useRef } from "react";
import type { Route } from "./+types/designers";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { BrilliantCut } from "~/components/marketing/svg/BrilliantCut";
import { ProcessFlow } from "~/components/marketing/svg/ProcessFlow";
import { JewelryIcon, type JewelryIconType } from "~/components/marketing/svg/JewelryIcons";
import { detectLocale, translations } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  return { locale };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = data?.locale ?? "en";
  const t = translations[locale].forDesigners.meta;
  return [
    { title: t.title },
    { name: "description", content: t.description },
    ...socialMeta({ title: t.title, description: t.description, pathname: "/designers", locale }),
  ];
}

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add("visible"); },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

export default function ForDesignersPage() {
  const { t } = useT();
  const h = t.forDesigners;

  const painRef = useReveal(0.1);
  const inspireRef = useReveal(0.1);
  const dotwRef = useReveal(0.1);
  const platformRef = useReveal();
  const collectionsRef = useReveal(0.1);
  const revenueRef = useReveal(0.1);
  const stepsRef = useReveal(0.1);
  const ctaRef = useReveal();

  const PLATFORM: { num: string; icon: JewelryIconType; heading: string; body: string }[] = [
    { num: "01", icon: "story-lab", heading: h.platform.items[0].heading, body: h.platform.items[0].body },
    { num: "02", icon: "viewer-3d", heading: h.platform.items[1].heading, body: h.platform.items[1].body },
    { num: "03", icon: "workspace", heading: h.platform.items[2].heading, body: h.platform.items[2].body },
    { num: "04", icon: "ip-lock", heading: h.platform.items[3].heading, body: h.platform.items[3].body },
  ];

  return (
    <MarketingLayout>

      {/* ══ HERO ══ */}
      <section className="px-6 lg:px-12 pt-16 pb-16 lg:pt-24 lg:pb-20 relative">
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={i} x1={`${(i + 1) * 9}%`} y1="0" x2={`${(i + 1) * 9}%`} y2="100%" stroke="oklch(0.085 0.004 50)" strokeWidth="1" opacity="0.025" />
          ))}
        </svg>

        <p className="issue-label mb-6 relative" style={{ fontFamily: "var(--font-sans)" }}>{h.hero.overline}</p>

        <h1 className="font-display font-light leading-[0.92] tracking-tight text-foreground max-w-4xl relative" style={{ fontSize: "clamp(3.5rem, 8vw, 7.5rem)" }}>
          {h.hero.h1Line1}
          <br />
          <em className="not-italic" style={{ fontWeight: 300, opacity: 0.42 }}>{h.hero.h1Line2}</em>
        </h1>

        <div className="mt-12 grid lg:grid-cols-[1fr_360px] items-start gap-8 relative">
          <div className="space-y-8">
            <p className="font-display font-light text-muted-foreground" style={{ fontSize: "clamp(1.05rem, 1.8vw, 1.4rem)", lineHeight: 1.5, maxWidth: "44ch" }}>
              {h.hero.tagline}
            </p>
            <a href="/inspire/creator-onboarding" className="inline-block px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>
              {h.hero.cta}
            </a>
          </div>
          <div className="hidden lg:block relative overflow-hidden bg-muted ml-auto" style={{ aspectRatio: "3/4", width: "100%", maxWidth: "320px" }}>
            <img src="https://images.unsplash.com/photo-1608042314453-ae338d80c427?auto=format&fit=crop&w=320&q=80" alt="" aria-hidden="true" className="w-full h-full object-cover" style={{ filter: "grayscale(0.10) contrast(1.06) saturate(0.85)" }} loading="eager" />
            <div className="absolute inset-[5px] border border-white/15 pointer-events-none" />
          </div>
        </div>
      </section>

      {/* ── Stat bar ── */}
      <div className="px-6 lg:px-12">
        <div className="rule" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
          {([
            ["80%", h.stats.share],
            [h.stats.ipProtected, h.stats.cadNote],
            [h.stats.perpetual, h.stats.royaltiesNote],
            [h.stats.zeroMarketing, h.stats.portfolioNote],
          ] as [string, string][]).map(([value, label], i) => (
            <div key={label} className={`py-5 ${i > 0 ? "pl-6 border-l border-border" : ""}`}>
              <p className="font-display font-light text-foreground leading-none mb-1" style={{ fontSize: i === 0 ? "clamp(1.6rem, 2.8vw, 2.4rem)" : "clamp(0.9rem, 1.6vw, 1.3rem)" }}>{value}</p>
              <p className="issue-label" style={{ fontFamily: "var(--font-sans)", opacity: 0.45 }}>{label}</p>
            </div>
          ))}
        </div>
        <div className="rule" />
      </div>

      {/* ══ PAIN HOOK ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 flex items-center pointer-events-none" aria-hidden="true">
          <BrilliantCut size={420} opacity={0.04} animated />
        </div>
        <div ref={painRef} className="reveal max-w-3xl relative">
          <span className="issue-label block mb-8" style={{ fontFamily: "var(--font-sans)" }}>{h.pain.overline}</span>
          <p className="font-display font-light text-foreground leading-snug" style={{ fontSize: "clamp(1.4rem, 2.8vw, 2.2rem)", lineHeight: 1.4 }}>{h.pain.p1}</p>
          <p className="font-display font-light mt-6" style={{ fontSize: "clamp(1.1rem, 2vw, 1.65rem)", lineHeight: 1.5, color: "var(--muted-foreground)" }}>{h.pain.p2}</p>
          <div className="mt-10 flex items-start gap-6">
            <div className="w-1 self-stretch flex-shrink-0 rounded-full" style={{ background: "var(--teal-sapphire)", opacity: 0.7, minHeight: "2.5rem" }} />
            <div>
              <p className="font-display font-light italic" style={{ fontSize: "clamp(1.15rem, 1.8vw, 1.5rem)", lineHeight: 1.5, color: "var(--foreground)" }}>"{h.pain.quote}"</p>
              <p className="mt-2 text-muted-foreground" style={{ fontSize: "0.72rem", fontFamily: "var(--font-sans)", letterSpacing: "0.12em" }}>{h.pain.quoteAttribution}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ INSPIRE — AI STORY ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-24 relative overflow-hidden">
        <div ref={inspireRef} className="reveal relative">
          <div className="flex items-baseline justify-between mb-2">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.inspire.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.inspire.subtitle}</span>
          </div>
          <h2 className="font-display font-light text-foreground mb-8" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", lineHeight: 1, maxWidth: "22ch" }}>
            {h.inspire.heading.split("\n").map((line: string, i: number) => (
              <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ opacity: 0.4 }}>{line}</em>}{i === 0 && <br />}</span>
            ))}
          </h2>

          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-16 items-start">
            <div className="space-y-5">
              <p className="font-display font-light text-foreground leading-snug" style={{ fontSize: "clamp(1.1rem, 1.8vw, 1.4rem)", lineHeight: 1.55 }}>
                {h.inspire.p1}
              </p>
              <p className="font-display font-light text-muted-foreground" style={{ fontSize: "clamp(1rem, 1.5vw, 1.2rem)", lineHeight: 1.6 }}>
                {h.inspire.p2}
              </p>

              {/* Simulated AI story output */}
              <div className="mt-6 border border-border/60 p-6 lg:p-8 relative">
                <div className="absolute top-0 left-6 -translate-y-1/2 px-3 bg-background">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground" style={{ fontFamily: "var(--font-sans)" }}>{h.inspire.previewLabel}</span>
                </div>
                <p className="font-display font-light text-foreground leading-snug mb-2" style={{ fontSize: "clamp(1.2rem, 2vw, 1.6rem)" }}>
                  "{h.inspire.previewTitle}"
                </p>
                <p className="text-xs italic text-muted-foreground mb-4">
                  {h.inspire.previewBody}
                </p>
                <p className="text-sm text-foreground/70 leading-relaxed mb-4">
                  {h.inspire.previewExtended}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {h.inspire.previewKeywords.map((kw) => (
                    <span key={kw} className="text-[10px] px-2 py-0.5 border border-border/60 text-muted-foreground/70">{kw}</span>
                  ))}
                </div>
              </div>

              <div className="pt-4">
                <a href="/inspire/creator-onboarding" className="inline-block px-6 py-3 bg-teal text-teal-fg hover:bg-teal-hover transition-colors" style={{ fontSize: "0.62rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>
                  {h.inspire.cta}
                </a>
              </div>
            </div>

            <div className="space-y-0">
              {h.inspire.features.map((feature: { label: string; description: string }, i: number) => (
                <div key={feature.label}>
                  <div className="rule" />
                  <div className="py-6">
                    <div className="flex items-baseline gap-4 mb-2">
                      <span className="font-display font-light select-none" style={{ fontSize: "1.6rem", lineHeight: 1, color: "var(--primary)", opacity: 0.4 }}>
                        {["I", "II", "III"][i]}
                      </span>
                      <h3 className="font-display font-normal text-foreground" style={{ fontSize: "1.05rem" }}>{feature.label}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed lg:ml-10" style={{ fontSize: "0.8rem", maxWidth: "36ch" }}>{feature.description}</p>
                  </div>
                </div>
              ))}
              <div className="rule" />
            </div>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ DESIGN OF THE WEEK ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 flex items-center pointer-events-none" aria-hidden="true">
          <BrilliantCut size={380} opacity={0.035} animated />
        </div>
        <div ref={dotwRef} className="reveal relative">
          <div className="flex items-baseline justify-between mb-2">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.designOfWeek.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.designOfWeek.subtitle}</span>
          </div>
          <h2 className="font-display font-light text-foreground mb-8" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", lineHeight: 1, maxWidth: "22ch" }}>
            {h.designOfWeek.heading.split("\n").map((line: string, i: number) => (
              <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ opacity: 0.4 }}>{line}</em>}{i === 0 && <br />}</span>
            ))}
          </h2>

          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16 items-start">
            {/* Left: benefits */}
            <div className="space-y-0">
              {h.designOfWeek.benefits.map((benefit: { label: string; description: string }, i: number) => (
                <div key={benefit.label}>
                  <div className="rule" />
                  <div className="py-6">
                    <div className="flex items-baseline gap-4 mb-2">
                      <span className="font-display font-light select-none" style={{ fontSize: "1.6rem", lineHeight: 1, color: "var(--teal-sapphire)", opacity: 0.5 }}>
                        {["I", "II", "III"][i]}
                      </span>
                      <h3 className="font-display font-normal text-foreground" style={{ fontSize: "1.05rem" }}>{benefit.label}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed lg:ml-10" style={{ fontSize: "0.8rem", maxWidth: "36ch" }}>{benefit.description}</p>
                  </div>
                </div>
              ))}
              <div className="rule" />
            </div>

            {/* Right: copy + simulated feature card */}
            <div className="space-y-5">
              <p className="font-display font-light text-foreground leading-snug" style={{ fontSize: "clamp(1.1rem, 1.8vw, 1.4rem)", lineHeight: 1.55 }}>
                {h.designOfWeek.p1}
              </p>
              <p className="font-display font-light text-muted-foreground" style={{ fontSize: "clamp(1rem, 1.5vw, 1.2rem)", lineHeight: 1.6 }}>
                {h.designOfWeek.p2}
              </p>

              {/* Simulated Design of the Week card */}
              <div className="mt-6 relative overflow-hidden bg-muted" style={{ aspectRatio: "4/3" }}>
                <img
                  src="https://images.unsplash.com/photo-1515562141589-67f0d569b6c3?auto=format&fit=crop&w=600&q=80"
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ filter: "grayscale(0.08) contrast(1.04) saturate(0.9)" }}
                  loading="lazy"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.085 0.004 50 / 0.85) 0%, transparent 60%)" }} />
                <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                  <span className="inline-block px-2.5 py-1 mb-3 text-[10px] tracking-[0.2em] uppercase" style={{ fontFamily: "var(--font-sans)", background: "var(--teal-sapphire)", color: "var(--teal-sapphire-fg)" }}>
                    {h.designOfWeek.badgeLabel}
                  </span>
                  <p className="font-display font-light leading-tight" style={{ fontSize: "clamp(1.3rem, 2.2vw, 1.8rem)", color: "oklch(0.978 0.008 90)" }}>
                    {h.designOfWeek.featuredTitle}
                  </p>
                  <p className="mt-1 text-xs" style={{ color: "oklch(0.7 0.01 90)" }}>
                    {h.designOfWeek.featuredAttribution}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ PLATFORM CAPABILITIES ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-20">
        <div ref={platformRef} className="reveal">
          <div className="flex items-baseline justify-between mb-10">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.platform.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.platform.subtitle}</span>
          </div>
          <div className="grid lg:grid-cols-4 gap-0">
            {PLATFORM.map((f, i) => (
              <div key={f.num} className="relative">
                {i > 0 ? <div className="hidden lg:block absolute inset-y-0 left-0 w-px bg-border" /> : null}
                <div className="py-8" style={{ paddingLeft: i > 0 ? "var(--col-pad)" : undefined, paddingRight: i < PLATFORM.length - 1 ? "var(--col-pad)" : undefined }}>
                  <div className="rule mb-5" />
                  <JewelryIcon type={f.icon} size={40} className="mb-4 opacity-50" />
                  <span className="font-display font-light select-none block mb-4" style={{ fontSize: "clamp(1.8rem, 2.5vw, 2.4rem)", lineHeight: 1, color: "var(--primary)", opacity: 0.5 }}>{f.num}</span>
                  <h3 className="font-display font-normal text-foreground leading-tight mb-3" style={{ fontSize: "clamp(1.05rem, 1.5vw, 1.3rem)" }}>{f.heading}</h3>
                  <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.8rem", maxWidth: "28ch" }}>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ AI COLLECTIONS ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 flex items-center pointer-events-none" aria-hidden="true">
          <BrilliantCut size={360} opacity={0.035} animated />
        </div>
        <div ref={collectionsRef} className="reveal relative">
          <div className="flex items-baseline justify-between mb-2">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.collections.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.collections.subtitle}</span>
          </div>
          <h2 className="font-display font-light text-foreground mb-8" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", lineHeight: 1, maxWidth: "22ch" }}>
            {h.collections.heading.split("\n").map((line: string, i: number) => (
              <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ opacity: 0.4 }}>{line}</em>}{i === 0 && <br />}</span>
            ))}
          </h2>

          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-16 items-start">
            <div className="space-y-5">
              <p className="font-display font-light text-foreground leading-snug" style={{ fontSize: "clamp(1.1rem, 1.8vw, 1.4rem)", lineHeight: 1.55 }}>
                {h.collections.p1}
              </p>
              <p className="font-display font-light text-muted-foreground" style={{ fontSize: "clamp(1rem, 1.5vw, 1.2rem)", lineHeight: 1.6 }}>
                {h.collections.p2}
              </p>
              <div className="pt-4">
                <a href="/studio/collections" className="inline-block px-6 py-3 bg-teal text-teal-fg hover:bg-teal-hover transition-colors" style={{ fontSize: "0.62rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>
                  {h.collections.cta}
                </a>
              </div>
            </div>

            <div className="space-y-0">
              {h.collections.features.map((feature: { label: string; description: string }, i: number) => (
                <div key={feature.label}>
                  <div className="rule" />
                  <div className="py-6">
                    <div className="flex items-baseline gap-4 mb-2">
                      <span className="font-display font-light select-none" style={{ fontSize: "1.6rem", lineHeight: 1, color: "var(--primary)", opacity: 0.4 }}>
                        {["I", "II", "III"][i]}
                      </span>
                      <h3 className="font-display font-normal text-foreground" style={{ fontSize: "1.05rem" }}>{feature.label}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed lg:ml-10" style={{ fontSize: "0.8rem", maxWidth: "36ch" }}>{feature.description}</p>
                  </div>
                </div>
              ))}
              <div className="rule" />
            </div>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ REVENUE MODELS ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-24 relative">
        <div ref={revenueRef} className="reveal">
          <div className="flex items-baseline justify-between mb-2">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.revenue.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.revenue.subtitle}</span>
          </div>
          <h2 className="font-display font-light text-foreground mb-12" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", lineHeight: 1, maxWidth: "20ch" }}>
            {h.revenue.heading.split("\n").map((line: string, i: number) => (
              <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ opacity: 0.4 }}>{line}</em>}{i === 0 && <br />}</span>
            ))}
          </h2>

          <div className="grid lg:grid-cols-3 gap-0">
            {h.revenue.models.map((m: { tier: string; label: string; heading: string; description: string; benefit: string }, i: number) => (
              <div key={m.tier} className="relative" style={i === 2 ? { background: "oklch(0.085 0.004 50)", color: "oklch(0.978 0.008 90)", margin: "0 -1px", zIndex: 1 } : undefined}>
                {i !== 2 && i > 0 ? <div className="hidden lg:block absolute inset-y-0 left-0 w-px bg-border" /> : null}
                <div className="p-8 lg:p-10 h-full flex flex-col gap-5">
                  <div className="flex items-center justify-between">
                    <span className="issue-label" style={{ fontFamily: "var(--font-sans)", color: i === 2 ? "var(--primary)" : undefined, opacity: i === 2 ? 1 : 0.45 }}>{m.label}</span>
                    {i === 2 && <span className="issue-label px-2 py-0.5" style={{ fontFamily: "var(--font-sans)", border: "1px solid var(--primary)", color: "var(--primary)", fontSize: "0.6rem", letterSpacing: "0.2em" }}>{h.badges.premium}</span>}
                  </div>
                  <span className="font-display font-light select-none" style={{ fontSize: "clamp(2.5rem, 4vw, 3.8rem)", lineHeight: 1, color: "var(--primary)", opacity: i === 2 ? 0.6 : 0.35 }}>0{i + 1}</span>
                  <h3 className="font-display font-normal leading-tight" style={{ fontSize: "clamp(1.4rem, 2vw, 1.9rem)", color: i === 2 ? "oklch(0.978 0.008 90)" : "var(--foreground)" }}>{m.heading}</h3>
                  <p className="leading-relaxed flex-1" style={{ fontSize: "0.84rem", color: i === 2 ? "oklch(0.7 0.01 90)" : "var(--muted-foreground)", maxWidth: "32ch" }}>{m.description}</p>
                  <p className="issue-label pt-4 border-t" style={{ fontFamily: "var(--font-sans)", borderColor: i === 2 ? "oklch(0.25 0.01 50)" : "var(--border)", color: i === 2 ? "var(--primary)" : "var(--foreground)", opacity: i === 2 ? 0.8 : 0.5 }}>{m.benefit}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-10 text-muted-foreground" style={{ fontSize: "0.78rem", fontFamily: "var(--font-sans)", lineHeight: 1.7, maxWidth: "60ch" }}>{h.revenue.footnote}</p>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ HOW IT WORKS ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-20">
        <div ref={stepsRef} className="reveal">
          <div className="flex items-baseline justify-between mb-10">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.steps.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.steps.subtitle}</span>
          </div>
          <ProcessFlow steps={3} className="mb-6 hidden lg:block" />
          <div className="grid lg:grid-cols-3 gap-0">
            {h.steps.items.map((step: { heading: string; body: string }, i: number) => (
              <div key={i} className="relative">
                {i > 0 ? <div className="hidden lg:block absolute inset-y-0 left-0 w-px bg-border" /> : null}
                <div className="py-8" style={{ paddingLeft: i > 0 ? "var(--col-pad)" : undefined, paddingRight: i < h.steps.items.length - 1 ? "var(--col-pad)" : undefined }}>
                  <div className="rule mb-6" />
                  <div className="flex items-baseline gap-3 mb-5">
                    <span className="font-display font-light select-none" style={{ fontSize: "clamp(2rem, 3vw, 2.8rem)", lineHeight: 1, color: "var(--primary)", opacity: 0.5 }}>
                      {["I", "II", "III"][i]}
                    </span>
                    <h3 className="font-display font-normal text-foreground" style={{ fontSize: "clamp(1.2rem, 1.8vw, 1.5rem)" }}>{step.heading}</h3>
                  </div>
                  <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.82rem", maxWidth: "30ch" }}>{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ BOTTOM LINE ══ */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-[1fr_440px]">
          <div className="px-6 lg:px-12 py-16 lg:py-24 relative flex flex-col justify-center">
            <div className="absolute inset-0 flex items-center pointer-events-none pl-12" aria-hidden="true">
              <BrilliantCut size={300} opacity={0.05} animated />
            </div>
            <div className="max-w-xl relative">
              <span className="issue-label block mb-6" style={{ fontFamily: "var(--font-sans)" }}>{h.bottomLine.overline}</span>
              <p className="font-display font-light text-foreground" style={{ fontSize: "clamp(1.4rem, 2.8vw, 2.2rem)", lineHeight: 1.4 }}>{h.bottomLine.p1}</p>
              <p className="font-display font-light text-muted-foreground mt-4" style={{ fontSize: "clamp(1.1rem, 2vw, 1.65rem)", lineHeight: 1.5 }}>{h.bottomLine.p2}</p>
              <div className="mt-8 flex items-center gap-5">
                <div className="text-center shrink-0">
                  <p className="font-display font-light text-foreground leading-none" style={{ fontSize: "clamp(2.2rem, 3.5vw, 3rem)" }}>80%</p>
                  <p className="issue-label mt-1" style={{ fontFamily: "var(--font-sans)", opacity: 0.5, fontSize: "0.58rem", letterSpacing: "0.18em" }}>{h.bottomLine.shareLabel}</p>
                </div>
                <div style={{ width: "1px", alignSelf: "stretch", background: "var(--border)" }} />
                <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.82rem" }}>{h.bottomLine.shareNote}</p>
              </div>
            </div>
          </div>
          <div className="relative hidden lg:block overflow-hidden bg-muted" style={{ minHeight: "380px" }}>
            <img src="https://images.unsplash.com/photo-1611652022419-a9419f74343d?auto=format&fit=crop&w=440&h=500&q=80" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "grayscale(0.10) contrast(1.05) saturate(0.85)" }} loading="lazy" />
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ CTA ══ */}
      <section className="px-6 lg:px-12 py-20 lg:py-28">
        <div ref={ctaRef} className="reveal grid lg:grid-cols-[1fr_auto] items-end gap-8">
          <div>
            <span className="issue-label block mb-5" style={{ fontFamily: "var(--font-sans)" }}>{h.cta.overline}</span>
            <h2 className="font-display font-light leading-[0.92] tracking-tight text-foreground" style={{ fontSize: "clamp(2.8rem, 6vw, 5.5rem)" }}>
              {h.cta.heading.split("\n").map((line: string, i: number) => (
                <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ fontWeight: 300, opacity: 0.45 }}>{line}</em>}{i === 0 && <br />}</span>
              ))}
            </h2>
          </div>
          <div className="pb-2 space-y-4">
            <a href="/inspire/creator-onboarding" className="block px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors text-center" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>
              {h.cta.primary}
            </a>
            <a href="/studio/inbox" className="block text-center issue-label hover:text-foreground transition-colors" style={{ fontFamily: "var(--font-sans)", opacity: 0.45 }}>
              {h.cta.secondary}
            </a>
          </div>
        </div>
      </section>

    </MarketingLayout>
  );
}
